import { useState, useCallback, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { GgcButton } from '@/components/ui/ggc/button';
import { useI18nText } from '@/lib/utils/i18nText';
import { cn } from '@/lib/utils/classNames';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 다이얼로그 제목 */
  title: string;
  /** 다이얼로그 본문 (ReactNode 또는 문자열) */
  children?: ReactNode;
  /** 확인 버튼 라벨 (미지정 시 destructive → '삭제', 아니면 '확인') */
  confirmLabel?: string;
  /** true 이면 타이틀·확인 버튼이 빨간색 파괴적 스타일 */
  destructive?: boolean;
  /** 실행 중일 때 표시할 노트 (optional) */
  processingNote?: string;
  /** 삭제/확인 API 호출. throw 시 에러 인라인 표시. 성공 시 자동 닫힘 */
  onConfirm: () => Promise<void>;
  /** 에러 발생 시 인라인 에러 아래에 표시할 보조 액션 (예: 편집 화면으로 이동) */
  errorAction?: { label: ReactNode; onClick: () => void };
  /** 기본 sm:max-w-sm 대신 적용할 너비 등 (본문 미리보기처럼 넓은 내용이 필요할 때) */
  contentClassName?: string;
}

export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  children,
  confirmLabel,
  destructive = false,
  processingNote,
  onConfirm,
  errorAction,
  contentClassName,
}: ConfirmDialogProps) {
  const { t } = useI18nText();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = useCallback((v: boolean) => {
    if (!v && running) return;
    if (!v) setError(null);
    onOpenChange(v);
  }, [running, onOpenChange]);

  const handleConfirm = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      setError((err as { message?: string })?.message ?? String(err));
    } finally {
      setRunning(false);
    }
  }, [onConfirm, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={cn('sm:max-w-sm', contentClassName)}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2${destructive ? ' text-ggc-red' : ''}`}>
            {destructive && <i className="fa fa-exclamation-triangle text-sm" />}
            {title}
          </DialogTitle>
          {/* Radix requires an accessible description (aria-describedby). Kept hidden and
              separate from the visible body below so it never affects that body's styling. */}
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>

        {children && (
          <div className="py-1 text-sm text-ggc-text-primary">
            {children}
          </div>
        )}

        {running && processingNote && (
          <div className="flex items-start gap-2 rounded-ggc-sm border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-ggc-sub-amber">
            <i className="fa fa-spinner fa-spin mt-0.5 shrink-0" />
            <span>{processingNote}</span>
          </div>
        )}

        {error && (
          <div className="rounded-ggc-sm border border-ggc-red bg-ggc-red-light px-3 py-2 text-sm text-ggc-red">
            <i className="fa fa-times-circle mr-1" />
            {error}
            {errorAction && (
              <div className="mt-2">
                <GgcButton type="button" variant="secondary" size="sm" onClick={errorAction.onClick}>
                  {errorAction.label}
                </GgcButton>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
          <GgcButton variant="secondary" disabled={running} onClick={() => onOpenChange(false)}>
            {t('Cancel', '취소')}
          </GgcButton>
          <GgcButton
            variant={destructive ? 'danger' : 'primary'}
            disabled={running}
            onClick={() => void handleConfirm()}
          >
            {running
              ? <><i className="fa fa-spinner fa-spin mr-1" />{t('Processing...', '처리 중...')}</>
              : (confirmLabel ?? (destructive
                  ? <><i className="fa fa-trash mr-1" />{t('Delete', '삭제')}</>
                  : t('Confirm', '확인')))}
          </GgcButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
